import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';
import { defaultContent } from '../data/defaultContent';
import { MdLightMode, MdDarkMode } from 'react-icons/md';
import { FaWhatsapp, FaInstagram, FaEnvelope, FaMapMarkerAlt, FaBars, FaTimes } from 'react-icons/fa';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { usePageMeta } from '../hooks/usePageMeta';
import Breadcrumbs from '../components/Breadcrumbs';

const WHATSAPP_URL = 'https://wa.me/5581988856777?text=Ol%C3%A1!%20Gostaria%20de%20agendar%20uma%20consulta.';

function LogoWatermark({ className = '', size = 320, opacity = 0.15 }) {
  const { darkMode } = useTheme();
  return (
    <img
      src={darkMode ? '/logo-dark.png' : '/logo-white.png'}
      alt="" aria-hidden="true"
      className={`pointer-events-none select-none ${className}`}
      style={{ width: size, height: size, opacity }}
    />
  );
}

function LogoDivider() {
  const { darkMode } = useTheme();
  return (
    <div className="flex items-center justify-center py-6 sm:py-10 px-5">
      <span className="flex-1 h-px bg-emerald-200/60 dark:bg-emerald-700/40" />
      <img src={darkMode ? '/logo-dark.png' : '/logo-white.png'} alt="" aria-hidden="true"
        className="mx-4 sm:mx-6 w-8 h-8 sm:w-10 sm:h-10 opacity-50 dark:opacity-40" />
      <span className="flex-1 h-px bg-emerald-200/60 dark:bg-emerald-700/40" />
    </div>
  );
}

const Sobre = () => {
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme();
  const { content } = useContent('sobre');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  usePageMeta({
    title: 'Sobre Gabriel de Carli — Psicólogo Clínico em Recife | Formação e Abordagem',
    description: 'Conheça Gabriel de Carli, psicólogo clínico (CRP 02/29417) em Recife. Formado pela UNIFAFIRE, especializado em Abordagem Centrada na Pessoa (ACP) de Carl Rogers. Atende ansiedade, depressão, autoestima, luto, traumas e mais.',
    path: '/sobre',
  });

  const pageContent = content || defaultContent.sobre;
  const profile = pageContent?.profile || defaultContent.sobre.profile;
  const sections = pageContent?.sections || defaultContent.sobre.sections;
  const contact = pageContent?.contact || defaultContent.sobre.contact;
  const scrollRef = useScrollReveal();

  return (
    <div ref={scrollRef} className="min-h-screen font-sans bg-stone-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100 scroll-smooth">

      {/* ─── NAVIGATION (shared pattern) ─── */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/90 dark:bg-gray-900/95 shadow-sm backdrop-blur-xl py-2'
          : 'bg-transparent py-5'
      }`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 group">
              <img src={darkMode ? '/logo-dark.png' : '/logo-white.png'} alt="Gabriel de Carli Psicólogo Clínico"
                className={`transition-all duration-300 ${isScrolled ? 'h-9 w-9' : 'h-11 w-11'}`} />
              <div>
                <span className={`block font-serif font-semibold transition-all duration-300 ${
                  isScrolled ? 'text-lg text-emerald-800 dark:text-white' : 'text-xl text-white'
                }`}>Gabriel de Carli</span>
                <span className={`block text-[11px] tracking-widest uppercase transition-all duration-300 ${
                  isScrolled ? 'text-emerald-600/70 dark:text-emerald-400/70' : 'text-white/70'
                }`}>Psicólogo Clínico</span>
              </div>
            </button>

            <div className="hidden md:flex items-center gap-8">
              {[
                { label: 'Início', action: () => navigate('/') },
                { label: 'Sobre', action: () => navigate('/sobre') },
              ].map(item => (
                <button key={item.label} onClick={item.action}
                  className={`text-sm font-medium tracking-wide transition-colors duration-300 relative group ${
                    isScrolled
                      ? 'text-gray-600 dark:text-gray-300 hover:text-emerald-700 dark:hover:text-emerald-400'
                      : 'text-white/90 hover:text-white'
                  }`}>
                  {item.label}
                  <span className={`absolute -bottom-1 left-0 w-0 h-px transition-all duration-300 group-hover:w-full ${
                    isScrolled ? 'bg-emerald-600 dark:bg-emerald-400' : 'bg-white'
                  }`} />
                </button>
              ))}
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
                className={`text-sm font-medium px-5 py-2 rounded-full transition-all duration-300 ${
                  isScrolled ? 'bg-emerald-700 text-white hover:bg-emerald-800' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}>Agendar Consulta</a>
              <button onClick={toggleTheme} aria-label="Alternar tema"
                className={`p-2 rounded-full transition-colors duration-300 ${
                  isScrolled ? 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800' : 'text-white/80 hover:bg-white/10'
                }`}>
                {darkMode ? <MdLightMode className="w-5 h-5" /> : <MdDarkMode className="w-5 h-5" />}
              </button>
            </div>

            <div className="md:hidden flex items-center gap-2">
              <button onClick={toggleTheme} aria-label="Alternar tema"
                className={`p-2 rounded-full transition-colors ${isScrolled ? 'text-gray-600 dark:text-gray-300' : 'text-white'}`}>
                {darkMode ? <MdLightMode className="w-5 h-5" /> : <MdDarkMode className="w-5 h-5" />}
              </button>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-md transition-colors ${isScrolled ? 'text-gray-600 dark:text-gray-300' : 'text-white'}`}
                aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}>
                {isMenuOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-56 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}>
            <div className={`rounded-xl p-4 space-y-2 ${
              isScrolled ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white/10 backdrop-blur-xl'
            }`}>
              {['Início', 'Sobre'].map(label => (
                <button key={label}
                  onClick={() => { navigate(label === 'Início' ? '/' : '/sobre'); setIsMenuOpen(false); }}
                  className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isScrolled ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700' : 'text-white hover:bg-white/10'
                  }`}>{label}</button>
              ))}
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}
                className="block w-full text-center px-4 py-2.5 bg-emerald-700 text-white rounded-lg text-sm font-medium hover:bg-emerald-800 transition-colors">
                Agendar Consulta
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── HERO: PROFILE ─── */}
      <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-20 overflow-hidden bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950">
        <div className="absolute inset-0 flex items-center justify-center">
          <LogoWatermark size={500} opacity={0.18} className="dark:opacity-[0.10] max-w-[80vw] max-h-[80vw] sm:max-w-none sm:max-h-none" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 to-transparent dark:from-gray-950/60" />

        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="hero-animate relative inline-block mb-6 sm:mb-8">
            <div className="absolute -inset-4 sm:-inset-6 rounded-full bg-emerald-400/20 dark:bg-emerald-500/10 blur-2xl animate-pulse" />
            <img src="/gabriel-profile.jpg" alt="Gabriel de Carli - Psicólogo Clínico em Recife CRP 02/29417"
              className="relative w-32 h-32 sm:w-48 sm:h-48 rounded-full object-cover border-4 border-white/20 shadow-2xl" />
            <img src="/logo-white.png" alt="" aria-hidden="true"
              className="absolute -bottom-1 -right-1 w-10 h-10 sm:w-14 sm:h-14 opacity-50" />
          </div>

          <h1 className="hero-animate hero-animate-delay-1 font-serif text-3xl sm:text-5xl font-semibold text-white leading-tight mb-2 sm:mb-3">
            {profile.title}
          </h1>
          <p className="hero-animate hero-animate-delay-2 text-emerald-200/70 text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.25em] uppercase">
            {profile.subtitle}
          </p>
        </div>
      </section>

      {/* ─── BREADCRUMBS ─── */}
      <Breadcrumbs items={[
        { label: 'Início', path: '/' },
        { label: 'Sobre', path: '/sobre' },
      ]} />

      {/* ─── LOGO DIVIDER ─── */}
      <LogoDivider />

      {/* ─── ABOUT SECTIONS ─── */}
      <section className="py-10 sm:py-16 px-4 sm:px-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20">
          <LogoWatermark size={400} opacity={0.10} />
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">

            {/* Formação */}
            <div className="reveal fade-up relative group p-5 sm:p-8 bg-white dark:bg-gray-900 rounded-2xl
                         border border-emerald-100 dark:border-gray-800
                         hover:shadow-xl hover:shadow-emerald-100/50 dark:hover:shadow-none
                         hover:border-emerald-200 dark:hover:border-emerald-800
                         transition-all duration-500">
              <img src={darkMode ? '/logo-dark.png' : '/logo-white.png'} alt="" aria-hidden="true"
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 sm:w-10 sm:h-10 opacity-[0.18] group-hover:opacity-[0.30] transition-opacity duration-500" />
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-emerald-800 dark:text-emerald-300 mb-3 sm:mb-4 pr-8">
                {sections.formacao.title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-light text-sm sm:text-base">
                {sections.formacao.content}
              </p>
            </div>

            {/* Áreas de Atuação */}
            {sections?.areas?.title && (
              <div className="reveal fade-up delay-100 relative group p-5 sm:p-8 bg-white dark:bg-gray-900 rounded-2xl
                           border border-emerald-100 dark:border-gray-800
                           hover:shadow-xl hover:shadow-emerald-100/50 dark:hover:shadow-none
                           hover:border-emerald-200 dark:hover:border-emerald-800
                           transition-all duration-500">
                <img src={darkMode ? '/logo-dark.png' : '/logo-white.png'} alt="" aria-hidden="true"
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 sm:w-10 sm:h-10 opacity-[0.18] group-hover:opacity-[0.30] transition-opacity duration-500" />
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-emerald-800 dark:text-emerald-300 mb-3 sm:mb-4 pr-8">
                  {sections.areas.title}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                  {sections.areas.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 font-light">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 dark:bg-emerald-600 shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Abordagem */}
            <div className="reveal fade-up delay-200 relative group p-5 sm:p-8 bg-white dark:bg-gray-900 rounded-2xl
                         border border-emerald-100 dark:border-gray-800
                         hover:shadow-xl hover:shadow-emerald-100/50 dark:hover:shadow-none
                         hover:border-emerald-200 dark:hover:border-emerald-800
                         transition-all duration-500">
              <img src={darkMode ? '/logo-dark.png' : '/logo-white.png'} alt="" aria-hidden="true"
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 sm:w-10 sm:h-10 opacity-[0.18] group-hover:opacity-[0.30] transition-opacity duration-500" />
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-emerald-800 dark:text-emerald-300 mb-3 sm:mb-4 pr-8">
                {sections.abordagem.title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-light text-sm sm:text-base">
                {sections.abordagem.content}
              </p>
            </div>

            {/* Contato */}
            <div className="reveal fade-up delay-300 relative group p-5 sm:p-8 bg-white dark:bg-gray-900 rounded-2xl
                         border border-emerald-100 dark:border-gray-800
                         hover:shadow-xl hover:shadow-emerald-100/50 dark:hover:shadow-none
                         hover:border-emerald-200 dark:hover:border-emerald-800
                         transition-all duration-500">
              <img src={darkMode ? '/logo-dark.png' : '/logo-white.png'} alt="" aria-hidden="true"
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 sm:w-10 sm:h-10 opacity-[0.18] group-hover:opacity-[0.30] transition-opacity duration-500" />
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-emerald-800 dark:text-emerald-300 mb-3 sm:mb-4 pr-8">
                Contato e Localização
              </h2>
              <div className="space-y-2.5 sm:space-y-3">
                {[
                  { icon: FaMapMarkerAlt, value: contact.address, href: `https://maps.google.com/?q=${encodeURIComponent(contact.address)}` },
                  { icon: FaEnvelope, value: contact.email, href: `mailto:${contact.email}` },
                  { icon: FaWhatsapp, value: contact.whatsapp, href: `https://wa.me/${contact.whatsapp.replace(/\D/g, '')}` },
                  { icon: FaInstagram, value: contact.instagram, href: `https://instagram.com/${contact.instagram.replace('@', '')}` },
                ].map((item, i) => (
                  <a key={i} href={item.href} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors duration-300 group/link">
                    <span className="w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0
                                   group-hover/link:bg-emerald-100 dark:group-hover/link:bg-emerald-900/50 transition-colors">
                      <item.icon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    </span>
                    <span className="text-xs sm:text-sm font-light">{item.value}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-12 sm:py-20 px-4 sm:px-8 relative overflow-hidden">
        <div className="relative max-w-3xl mx-auto">
          <div className="reveal scale-in relative p-8 sm:p-16 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-800 dark:from-emerald-900 dark:to-gray-900 text-white text-center overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <LogoWatermark size={350} opacity={0.20} className="brightness-200" />
            </div>
            <div className="relative">
              <h2 className="font-serif text-2xl sm:text-4xl font-semibold mb-3 sm:mb-4">
                Vamos conversar?
              </h2>
              <p className="text-emerald-100/70 font-light max-w-md mx-auto mb-6 sm:mb-10 leading-relaxed text-sm sm:text-base">
                A primeira sessão é gratuita. Juntos, podemos iniciar uma jornada terapêutica que faça sentido para você.
              </p>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-emerald-800 hover:bg-emerald-50
                         px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-xs sm:text-sm font-semibold tracking-wide
                         transition-all duration-300 hover:shadow-lg">
                <FaWhatsapp className="w-4 h-4" />
                Agendar pelo WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="reveal fade-in bg-emerald-900 dark:bg-gray-900 text-white py-10 sm:py-14 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <LogoWatermark size={200} opacity={0.15} className="brightness-200" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col items-center gap-6">
            <img src="/logo-white.png" alt="Gabriel de Carli Psicólogo Clínico" className="w-16 h-16 opacity-80" />
            <div className="text-center">
              <p className="font-serif text-lg font-medium">Gabriel de Carli</p>
              <p className="text-emerald-300/60 text-xs tracking-widest uppercase mt-1">Psicólogo Clínico — CRP 02/29417</p>
            </div>
            <div className="flex gap-4">
              <a href={`https://instagram.com/${contact.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-emerald-500/30 hover:scale-110 flex items-center justify-center transition-all duration-300">
                <FaInstagram className="w-4 h-4" />
              </a>
              <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-emerald-500/30 hover:scale-110 flex items-center justify-center transition-all duration-300">
                <FaWhatsapp className="w-4 h-4" />
              </a>
              <a href={`mailto:${contact.email}`}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-emerald-500/30 hover:scale-110 flex items-center justify-center transition-all duration-300">
                <FaEnvelope className="w-4 h-4" />
              </a>
            </div>
            <p className="text-emerald-300/40 text-xs mt-4">
              © {new Date().getFullYear()} Gabriel de Carli — Todos os direitos reservados
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Sobre;
